import { RedoIcon } from '@/components/icons/redo-icon'
import { SparklesIcon } from '@/components/icons/sparkles-icon'
import { UndoIcon } from '@/components/icons/undo-icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { T } from '@tolgee/react'
import { AlertCircle, AlertTriangle, Check, Info, X } from 'lucide-react'
import React from 'react'
import {
  ENRICHMENT_AGENTS,
  ENRICHMENT_METADATA_TYPES,
  ENRICHMENT_TYPES,
  EnrichmentAgent,
  EnrichmentMetadataType,
  EnrichmentType,
} from '../constants'
import { useDataCatalogNavigation } from '../hooks/use-data-catalog-breadcrumbs'
import { EnrichmentControlsState, EnrichmentWorkflow, TableViewType, TaggingFlags } from '../types'
import {
  AGENT_OPTIONS,
  areSensitivityAndDQTagsNull,
  getDefaultMetadataType,
  getMetadataSelectOptions,
} from '../utils'
import { EnrichmentProgress } from './enrichment-progress'

interface ClearAllConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
  isClearingMetadata: boolean
}

interface EnrichmentControlsProps {
  type: EnrichmentType
  isNoData: boolean
  enrichmentState: EnrichmentControlsState
  setEnrichmentState: React.Dispatch<React.SetStateAction<EnrichmentControlsState>>
  enrichmentWorkflow: EnrichmentWorkflow
  hasEmptyMetadataFields?: boolean
  onEnrich: () => void
  onCancel: () => void
  onSave: () => void
  onUndoEnrichment: () => void
  onRedoEnrichment: () => void
  onClearAllMetadata: () => Promise<void>
  disableClearAllMetadata: boolean
  isSaving: boolean
  isClearingMetadata?: boolean
  hasLocalEdits?: boolean
  onClearLocalEdits?: () => void
  onSaveEdits?: () => void
  isSavingEdits?: boolean
  tableCurrentView?: TableViewType
  latestUpdate?: string | null
  taggingFlags?: TaggingFlags
}

export const EnrichmentControls = ({
  type,
  isNoData,
  enrichmentState,
  setEnrichmentState,
  enrichmentWorkflow,
  hasEmptyMetadataFields = false,
  onEnrich,
  onCancel,
  onSave,
  onUndoEnrichment,
  onRedoEnrichment,
  onClearAllMetadata,
  disableClearAllMetadata,
  isSaving,
  isClearingMetadata = false,
  hasLocalEdits = false,
  onClearLocalEdits,
  onSaveEdits,
  isSavingEdits = false,
  tableCurrentView = 'metadata',
  latestUpdate = null,
  taggingFlags,
}: EnrichmentControlsProps) => {
  const [showClearAllDialog, setShowClearAllDialog] = React.useState(false)
  const prevTableCurrentView = React.useRef(tableCurrentView)
  const { openIndustryReferenceTab } = useDataCatalogNavigation()

  const handleClearAllClick = () => {
    setShowClearAllDialog(true)
  }

  const handleClearAllConfirm = async () => {
    try {
      await onClearAllMetadata()
      setShowClearAllDialog(false) // Only close dialog after successful API call
    } catch (error) {
      // Keep dialog open on error - error handling should be done in onClearAllMetadata
      console.error('Failed to clear metadata:', error)
    }
  }

  // Update selected metadata when table view changes
  React.useEffect(() => {
    if (
      enrichmentState.showEnrichmentControls &&
      prevTableCurrentView.current !== tableCurrentView
    ) {
      const defaultMetadataType = getDefaultMetadataType(type, tableCurrentView)
      enrichmentWorkflow.updateSelectedMetadata([defaultMetadataType])
      prevTableCurrentView.current = tableCurrentView
    }
  }, [tableCurrentView, enrichmentState.showEnrichmentControls, type, enrichmentWorkflow])

  if (isNoData) {
    return null
  }

  const isEnriching = enrichmentWorkflow.isEnrichmentInProcess
  const isEnrichingCompleted = enrichmentWorkflow.isEnrichmentCompleted
  const showEnrichedData =
    isEnrichingCompleted || enrichmentState.showEnrichmentControls || isEnriching
  const selectedTableIds = enrichmentWorkflow.state.selectedTableIds || []
  const isOriginalView = enrichmentWorkflow.state.currentView === 'original'

  const handleInitialEnrichmentClick = () => {
    setEnrichmentState((prev) => ({
      ...prev,
      showEnrichmentControls: true,
      selectedAgent: ENRICHMENT_AGENTS.EM_AGENTS,
    }))

    // Set default metadata based on enrichment type and table current view
    const defaultMetadataType = getDefaultMetadataType(type, tableCurrentView)
    enrichmentWorkflow.updateSelectedMetadata([defaultMetadataType])
  }

  const handleEnrichClick = () => {
    if ((enrichmentWorkflow.state.selectedMetadata?.length || 0) === 0) {
      toast.error('Please select at least one metadata type')
      return
    }
    if (!enrichmentState.selectedAgent) {
      toast.error('Please select an agent')
      return
    }
    if (type === ENRICHMENT_TYPES.TABLE && selectedTableIds.length === 0) {
      toast.error('Please select at least one table to enrich')
      return
    }
    onEnrich()
  }

  const renderMetadataSelector = () => (
    <div className='flex items-center gap-1'>
      <p className='font-light'>
        {tableCurrentView === 'metadata' ? 'Enrich metadata' : 'Generate rules and scripts'}
      </p>
      <Select
        value={
          enrichmentWorkflow.state.selectedMetadata?.[0] ||
          getDefaultMetadataType(type, tableCurrentView)
        }
        onValueChange={(val) =>
          enrichmentWorkflow.updateSelectedMetadata([val as EnrichmentMetadataType])
        }
      >
        <SelectTrigger isDirty className='w-60'>
          <SelectValue
            placeholder={
              type === ENRICHMENT_TYPES.TABLE ? (
                <T keyName='table.enrichment.allMetadata' />
              ) : (
                'Select metadata type'
              )
            }
          />
        </SelectTrigger>
        <SelectContent>
          {getMetadataSelectOptions(
            type,
            hasEmptyMetadataFields,
            tableCurrentView,
            taggingFlags,
          ).map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.value === ENRICHMENT_METADATA_TYPES.TABLE_DESCRIPTION ? (
                <T keyName='table.enrichment.descriptionOption' />
              ) : (
                option.label
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  const renderAgentSelector = () => {
    // Check if DQ rules are selected
    const isDQRulesSelected = enrichmentWorkflow.state.selectedMetadata?.includes(
      ENRICHMENT_METADATA_TYPES.DQ_RULES,
    )

    return (
      <div className='flex items-center gap-1'>
        <div className='flex items-center gap-1 font-light'>
          <T keyName='metadata.enrichment.basedOn' />
          <TooltipIconButton
            asChild
            size='auto'
            className='size-[18px] cursor-pointer hover:bg-transparent focus:bg-transparent active:bg-transparent'
            keyName='metadata.enrichment.industryMessage'
            onClick={openIndustryReferenceTab}
          >
            <Info className='shrink-0 text-neutral-150' />
          </TooltipIconButton>
        </div>
        <Select
          value={enrichmentState.selectedAgent}
          onValueChange={(val) =>
            setEnrichmentState((prev) => ({ ...prev, selectedAgent: val as EnrichmentAgent }))
          }
          disabled={isDQRulesSelected}
        >
          <SelectTrigger isDirty className='w-60' disabled={isDQRulesSelected}>
            <SelectValue placeholder={'Generative Enrichment'} />
          </SelectTrigger>
          <SelectContent>
            {AGENT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  const renderActionButtons = () => (
    <div className='flex items-center gap-4'>
      <Button
        variant='mango-outline'
        unstyled
        onClick={onUndoEnrichment}
        title={isOriginalView ? undefined : 'Undo Enrichment'}
        disabled={isOriginalView || !isEnrichingCompleted}
        className='text-neutral-500 disabled:text-neutral-200'
      >
        <UndoIcon />
      </Button>

      <Button
        variant='mango-outline'
        unstyled
        onClick={onRedoEnrichment}
        title={isOriginalView ? 'Redo Enrichment' : undefined}
        disabled={!isOriginalView || !isEnrichingCompleted}
        className='text-neutral-500 disabled:text-neutral-200'
      >
        <RedoIcon />
      </Button>

      <Button onClick={onSave} className='capitalize' disabled={isSaving || !isEnrichingCompleted}>
        {isSaving ? (
          <T keyName='metadata.enrichment.saving' />
        ) : (
          <T keyName='metadata.enrichment.save' />
        )}
        <Check className='size-5 text-mango-300' />
      </Button>

      <span className='h-10 border-l-2 border-neutral-50'></span>

      <Button
        variant='mango-outline'
        unstyled
        onClick={() => {
          onCancel()
        }}
        title={showEnrichedData ? 'Cancel enrichment and clear all data' : 'Close enrichment view'}
      >
        <X className='size-5' />
      </Button>
    </div>
  )

  const renderDataQualityWarning = () => (
    <div className='mb-4 flex items-center gap-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-3'>
      <AlertTriangle className='size-5 text-orange-500' />
      <div className='flex flex-col'>
        <p className='text-sm font-medium text-orange-900'>Waiting for metadata enrichment</p>
        <p className='text-sm text-orange-600'>
          Data quality enrichment requires completed metadata enrichment. Please finish enriching
          descriptions and business definitions in the metadata wizard before proceeding.
        </p>
      </div>
    </div>
  )

  const renderEditControls = () => (
    <div className='flex w-full flex-wrap items-center justify-between gap-4'>
      <div className='flex items-center gap-2'>
        <p className='font-light'>
          Review and edit all enriched metadata, then click Save to create a new version
        </p>
      </div>
      <div className='flex items-center gap-4'>
        <Button
          variant='mango-outline'
          unstyled
          onClick={() => {
            // Clear local edits
            if (hasLocalEdits) {
              onClearLocalEdits?.()
            }
          }}
          title='Cancel edits and revert changes'
        >
          <X className='size-5' />
        </Button>

        <Button onClick={onSaveEdits} className='capitalize' disabled={isSavingEdits}>
          {isSavingEdits ? (
            <T keyName='metadata.enrichment.saving' />
          ) : (
            <T keyName='metadata.enrichment.save' />
          )}
          <Check className='size-5 text-mango-300' />
        </Button>
      </div>
    </div>
  )

  const getEnrichButtonText = () => {
    if (isEnriching) {
      return tableCurrentView === 'metadata' ? (
        <T keyName='metadata.enrichment.enriching' />
      ) : (
        <T keyName='metadata.enrichment.generating' />
      )
    }

    return tableCurrentView === 'metadata' ? (
      <T keyName='metadata.enrichment.enrich' />
    ) : (
      <T keyName='metadata.enrichment.generate' />
    )
  }

  const renderEnrichedControls = () => (
    <div className='flex w-full flex-wrap items-center gap-4'>
      <div className='flex items-center gap-4'>
        {isEnriching ? (
          <EnrichmentProgress
            latestUpdate={latestUpdate}
            isEnriching={isEnriching}
            workflowId={enrichmentWorkflow.state.workflowId || undefined}
          />
        ) : (
          <>
            {renderMetadataSelector()}
            {renderAgentSelector()}
          </>
        )}
      </div>
      <div className='flex flex-1 items-center justify-between gap-4'>
        <Button
          shape={'round'}
          className='flex gap-1 capitalize disabled:pointer-events-auto'
          onClick={handleEnrichClick}
          title={
            type === ENRICHMENT_TYPES.TABLE && selectedTableIds.length === 0
              ? 'Please select at least one table to enrich'
              : tableCurrentView === 'dataQuality' &&
                  (hasEmptyMetadataFields || areSensitivityAndDQTagsNull(taggingFlags))
                ? 'Complete Metadata enrichment first to enable Data Quality.'
                : undefined
          }
          disabled={
            (type === ENRICHMENT_TYPES.TABLE && selectedTableIds.length === 0) ||
            isEnriching ||
            (tableCurrentView === 'dataQuality' &&
              (hasEmptyMetadataFields || areSensitivityAndDQTagsNull(taggingFlags)))
          }
        >
          <span>{getEnrichButtonText()}</span>
          <SparklesIcon className='text-mango-300' />
        </Button>

        {renderActionButtons()}
      </div>
    </div>
  )

  const renderInitialControls = () => (
    <div className='flex w-full items-center justify-between'>
      <div className='flex items-center gap-2'>
        <Button
          shape={'round'}
          className='flex gap-1 capitalize'
          onClick={handleInitialEnrichmentClick}
          title={
            tableCurrentView === 'dataQuality' &&
            (hasEmptyMetadataFields || areSensitivityAndDQTagsNull(taggingFlags))
              ? 'No data quality metadata available to enrich'
              : undefined
          }
        >
          <span>
            {tableCurrentView === 'metadata' ? 'Enrich metadata' : 'Generate Rules & Scripts'}
          </span>
          <SparklesIcon className='text-mango-300' />
        </Button>
        <p className='font-light'>
          {tableCurrentView === 'metadata'
            ? 'Enrich metadata using Emergence AI knowledge or industry standards.'
            : 'Use Emergence Data Quality Agents to generate tags, rules and SQL scripts.'}
        </p>
      </div>
      <Button
        unstyled
        onClick={handleClearAllClick}
        disabled={isClearingMetadata || disableClearAllMetadata}
        className='px-4 py-2 font-medium text-neutral-500 disabled:text-neutral-200'
      >
        Clear All
      </Button>
    </div>
  )

  const renderControls = () => {
    if (hasLocalEdits) {
      return renderEditControls()
    }
    if (showEnrichedData) {
      return renderEnrichedControls()
    }
    return renderInitialControls()
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      {tableCurrentView === 'dataQuality' &&
        (hasEmptyMetadataFields || areSensitivityAndDQTagsNull(taggingFlags)) &&
        renderDataQualityWarning()}

      <div className='flex w-full items-center justify-between rounded-md py-2 pl-2 pr-4 transition-colors duration-200 hover:bg-neutral-25'>
        <div className='flex w-full items-center justify-between'>{renderControls()}</div>
      </div>

      <ClearAllConfirmationDialog
        open={showClearAllDialog}
        onOpenChange={setShowClearAllDialog}
        onConfirm={handleClearAllConfirm}
        isClearingMetadata={isClearingMetadata}
      />
    </div>
  )
}

const ClearAllConfirmationDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isClearingMetadata,
}: ClearAllConfirmationDialogProps) => {
  const handleConfirm = async () => {
    try {
      await onConfirm()
      // Dialog will be closed by the parent component after successful API call
    } catch (error) {
      // Keep dialog open on error - parent component should handle error display
      console.error('Failed to clear metadata:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Clear all Metadata?</DialogTitle>
          <div className='space-y-2'>
            <DialogDescription className='flex items-center gap-2'>
              <AlertCircle className='size-4 text-error-300' />
              <span className='font-medium'>
                Clearing metadata is permanent. This action cannot be undone.
              </span>
            </DialogDescription>
            <DialogDescription>Are you sure you want to proceed?</DialogDescription>
          </div>
        </DialogHeader>
        <DialogFooter className='flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2'>
          <Button
            variant='mango-outline'
            onClick={() => onOpenChange(false)}
            disabled={isClearingMetadata}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isClearingMetadata}
            disabled={isClearingMetadata}
          >
            Clear all metadata
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
